import { CustomErrorSignificance } from "./Types/CustomErrorSignificance"

/**
 * Custom error object which can be used to either show in ErrorMessage or log
 */
class CustomError extends Error {

    /**
     * Creates CustomError object from various types of error objects
     *
     * @param object contains error information (e.g. JSON, Error)
     * @param errorObjectRequired if empty error message should be returned if no message could be extracted
     * @param significance error significance
     */
    public static fromObject = (
        object: any,
        errorObjectRequired = true,
        significance = CustomErrorSignificance.NonFatal): CustomError | undefined => {
        if (object === undefined) {
            return errorObjectRequired ? new CustomError('', significance) : undefined
        }

        if (object instanceof CustomError) {
            return object
        } else if (object instanceof Error) {
            return new CustomError(object.message, significance)
        } else if (object.error_message !== undefined) {
            return new CustomError(object.error_message, significance)
        } else if (object.message !== undefined) {
            if (object.message.text !== undefined) {
                return new CustomError(object.message.text, significance)
            }
            return new CustomError(object.message, significance)
        } else if (typeof object === 'string') {
            return new CustomError(object, significance)
        }

        return errorObjectRequired ? new CustomError('', significance) : undefined
    }

    /**
     * Creates CustomError from request error response
     *
     * @param status Status text
     * @param statusCode Status code
     * @todo Please document status code (HTTP codes, custom codes, ..., which system is used?)
     * @param url Url where request error appeared
     */
    public static withHttpError = (status: string, statusCode: number, url?: string) => {
        const significance = statusCode >= 500 ? CustomErrorSignificance.Fatal : CustomErrorSignificance.NonFatal
        return new CustomError(status, significance, statusCode, url)
    }

    public readonly message: string
    public readonly errorSignificance: CustomErrorSignificance
    public readonly statusCode?: number
    public readonly url?: string
    public readonly debugMessage?: string

    /**
     *
     * @param message Human readable error message
     * @param errorSignificance Fatal or non-fatal
     * @param statusCode status code
     * @param url url
     * @param debugMessage Technical message, can be used for debugging purposes
     */
    constructor(
        message: string,
        errorSignificance: CustomErrorSignificance = CustomErrorSignificance.NonFatal,
        statusCode?: number,
        url?: string,
        debugMessage?: string,
    ) {
        super(message)
        this.errorSignificance = errorSignificance

        this.message = message
        this.statusCode = statusCode
        this.url = url
        this.debugMessage = debugMessage

        Object.setPrototypeOf(this, new.target.prototype)
    }

    /**
     * Returns the custom errors status code
     * @returns int
     */
    public getStatusCode = () => {
        if (this.statusCode) {
            return this.statusCode
        }
        return -1
    }

    /**
     * Returns the custom error message
     * @returns string
     */
    public getMessage = () => {
        return this.message
    }

    public getDebugMessage = () => {
        return this.debugMessage
    }
}

export default CustomError
